import moment from 'moment'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { request, businessCode } from './index'

export type DayType = {[date: string]: Array<string>}

export type WlistType = {
  id: number
  service: string
  service_id: number
  resource: string
  resource_id: number
  date: string
  day: number
  from: string
  to: string
  month: number
  year: number
  allow_cancel: boolean
}

type StateType = {
  id: string | null
  enabled: boolean
  fetch: boolean
  dialog: string | null
  all: Array<WlistType>
  days: Array<DayType>
  month: number,
  year: number,
  selectedDay: number | null
  selectedFrom: string | null
  selectedTo: string | null
  cancelId: number | null
}

const initialState: StateType = {
  id: null,
  dialog: null,
  enabled: false,
  fetch: true,
  all: [],
  days: [],
  month: Number(moment().format('M')),
  year: Number(moment().format('YYYY')),
  selectedDay: null,
  selectedFrom: null,
  selectedTo: null,
  cancelId: null,
}

export const wlistSlice = createSlice({
  name: '$wlist',

  initialState,

  reducers: {
    id: (state: StateType, action: PayloadAction<string | null>) => {
      state.id = action.payload
    },

    enabled: (state: StateType, action: PayloadAction<boolean>) => {
      state.enabled = action.payload
    },

    dialog: (state: StateType, action: PayloadAction<string | null>) => {
      state.dialog = action.payload
    },

    days: (state: StateType, action: PayloadAction<Array<DayType>>) => {
      state.days = action.payload
    },

    month: (state: StateType, action: PayloadAction<{month: number, year: number, day?: number, from?: string, to?: string}>) => {
      state.month = action.payload.month
      state.year = action.payload.year
      state.selectedDay = action.payload?.day ?? null
      state.selectedFrom = action.payload?.from ?? null
      state.selectedTo = action.payload?.to ?? null
    },

    selectedDay: (state: StateType, action: PayloadAction<number | null>) => {
      state.selectedDay = action.payload
    },

    selectedFrom: (state: StateType, action: PayloadAction<string | null>) => {
      state.selectedFrom = action.payload
    },

    selectedTo: (state: StateType, action: PayloadAction<string | null>) => {
      state.selectedTo = action.payload
    },

    all: (state: StateType, action: PayloadAction<Array<any>>) => {
      state.all = action.payload.map(item => {
        return {
          id: item.id,
          service: item.item_name,
          service_id: item.item_id,
          resource: item.resource_name,
          resource_id: item.resource_id,
          date: moment(item.from).format('YYYY-MM-DD HH:mm'),
          day: Number(moment(item.from).format('D')),
          from: moment(item.from).format('HH:mm'),
          to: moment(item.to).format('HH:mm'),
          month: Number(moment(item.from).format('M')),
          year: Number(moment(item.from).format('YYYY')),
          allow_cancel: true,
        }
      })
    },

    fetch: (state: StateType, action: PayloadAction<boolean>) => {
      state.fetch = action.payload
    },

    cancelId: (state: StateType, action: PayloadAction<number | null>) => {
      state.cancelId = action.payload
    },

    reset: (state: StateType) => {
      state.id = null
      state.dialog = null
      state.enabled = false
      state.days = []
      state.month = Number(moment().format('M'))
      state.year = Number(moment().format('YYYY'))
      state.selectedDay = null
      state.selectedFrom = null
      state.selectedTo = null
      state.cancelId = null
    },
  },
})

const get = {
  days: createSelector([
    state => state.$wlist.days,
  ], (days) => {
    const keys = Object.keys(days)
    return keys.map(key => Number(moment(key, 'YYYY-MM-DD').format('D')))
  }),

  times: createSelector([
    state => state.$wlist.days,
    state => state.$wlist.year,
    state => state.$wlist.month,
    state => state.$wlist.selectedDay,
  ], (days, year, month, day) => {
    return day && days ? days[moment().year(year).month(month - 1).date(day).format('YYYY-MM-DD')] : []
  }),

  formatTime: (type: string) => createSelector([
    state => state.$wlist.year,
    state => state.$wlist.month,
    state => state.$wlist.selectedDay,
    state => state.$wlist.selectedFrom,
    state => state.$wlist.selectedTo,
  ], (year, month, day, from, to) => {
    const time = type === 'from' ? from : to
    if (day && time) {
      const [hours, minutes] = time.split(':').map(Number)

      return moment().year(year).month(month - 1).date(day).set({
        hour: hours,
        minute: minutes,
      }).format('YYYY-MM-DD HH:mm')
    }

    return null
  }),

  selectedDate: createSelector([
    state => state.$wlist.selectedDay,
    state => state.$wlist.month,
    state => state.$wlist.year,
  ], (day, month, year) => {
    return day ? `${day} ${moment().year(year).month(month - 1).date(day).format('MMMM')} ${year}` : null
  }),
}

const api = {
  days: (params: object) => {
    return request(`days/id/${businessCode()}`, 'POST', params)
  },

  add: (params: object) => {
    return request(`waiting_list/id/${businessCode()}`, 'POST', params)
  },

  get: (params: object) => {
    return request(`get_waiting_list/id/${businessCode()}`, 'POST', params)
  },

  all: (params: object) => {
    return request(`get_waiting_lists/id/${businessCode()}`, 'POST', params)
  },

  cancel: (params: object) => {
    return request(`remove_waiting_lists/id/${businessCode()}`, 'POST', params)
  },
}

const wlist = {
  reducer: wlistSlice.reducer,
  set: wlistSlice.actions,
  get,
  api,
}

export default wlist